/**
 * The jargon check and the readability figures for the Feynman technique template.
 *
 * Nothing here judges the explanation. It marks words a beginner might trip over: terms
 * the reader copied from their source, entries on the plain-language list, and long
 * words. A flagged word is a prompt to ask "could I say this more simply?", and a long
 * word is often exactly the right word, so the reader decides what stays.
 */

import { findComplexWords } from "@/lib/tool-kit/style-checkers/complex-words";
import { countSyllables } from "@/lib/tool-kit/style-checkers/syllables";

export type JargonKind = "source" | "plain" | "long";

export interface JargonSpan {
  start: number;
  end: number;
  kind: JargonKind;
  /** Plain-language alternatives from the list, for `plain` spans. */
  alternatives: string[];
}

export const MAX_SOURCE_TERMS = 40;
const MAX_TERM_LENGTH = 40;

const TERM_SPLIT = /[\n,;]+/;
const REGEX_SPECIALS = /[.*+?^${}()|[\]\\]/g;

/** The terms typed one per line (or separated by commas), trimmed and capped. */
export function parseSourceTerms(raw: string): string[] {
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const piece of raw.split(TERM_SPLIT)) {
    const term = piece.trim().slice(0, MAX_TERM_LENGTH);
    const key = term.toLowerCase();
    if (term && !seen.has(key)) {
      seen.add(key);
      terms.push(term);
    }
  }
  return terms.slice(0, MAX_SOURCE_TERMS);
}

function termSpans(text: string, terms: readonly string[]): JargonSpan[] {
  const spans: JargonSpan[] = [];
  for (const term of terms) {
    const escaped = term.replace(REGEX_SPECIALS, "\\$&");
    const pattern = new RegExp(
      `(?<![\\p{L}\\p{N}])${escaped}(?![\\p{L}\\p{N}])`,
      "giu"
    );
    for (const match of text.matchAll(pattern)) {
      spans.push({
        start: match.index,
        end: match.index + match[0].length,
        kind: "source",
        alternatives: [],
      });
    }
  }
  return spans;
}

function overlaps(a: JargonSpan, b: JargonSpan): boolean {
  return a.start < b.end && a.end > b.start;
}

/**
 * Every flagged span in `text`, sorted, with none overlapping. When two flags cover the
 * same words the reader's own source terms win over the plain-language list, and that
 * wins over the long-word test.
 */
export function findJargon(
  text: string,
  options: {
    sourceTerms: readonly string[];
    minSyllables: 3 | 4;
    /** Lowercase words the reader has marked as fine. */
    keep?: ReadonlySet<string>;
  }
): JargonSpan[] {
  const report = findComplexWords(text, {
    minSyllables: options.minSyllables,
    usePlainList: true,
    skipQuotes: false,
    keep: options.keep ?? new Set(),
  });
  const candidates: JargonSpan[] = [
    ...termSpans(text, options.sourceTerms),
    ...report.plain.map(
      (match): JargonSpan => ({
        start: match.start,
        end: match.end,
        kind: "plain",
        alternatives: match.alternatives,
      })
    ),
    ...report.long.map(
      (word): JargonSpan => ({
        start: word.start,
        end: word.end,
        kind: "long",
        alternatives: [],
      })
    ),
  ];
  const priority: Record<JargonKind, number> = { source: 0, plain: 1, long: 2 };
  candidates.sort(
    (a, b) => priority[a.kind] - priority[b.kind] || a.start - b.start
  );
  const kept: JargonSpan[] = [];
  for (const span of candidates) {
    if (!kept.some((other) => overlaps(span, other))) {
      kept.push(span);
    }
  }
  return kept.sort((a, b) => a.start - b.start);
}

export interface FlaggedWord {
  word: string;
  kind: JargonKind;
  count: number;
  alternatives: string[];
}

/** The flagged words once each, in order of first appearance, with how often they occur. */
export function summariseJargon(
  text: string,
  spans: readonly JargonSpan[]
): FlaggedWord[] {
  const byKey = new Map<string, FlaggedWord>();
  for (const span of spans) {
    const word = text.slice(span.start, span.end);
    const key = `${span.kind}:${word.toLowerCase()}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      byKey.set(key, {
        word,
        kind: span.kind,
        count: 1,
        alternatives: span.alternatives,
      });
    }
  }
  return [...byKey.values()];
}

/* ------------------------------------------------------------------ readability */

const WORD_PATTERN = /[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu;
const SENTENCE_END = /[.!?…]+(?=\s|$)/g;
const ENDS_WITH_WORD = /[\p{L}\p{N}]\s*$/u;

export interface ExplanationStats {
  words: number;
  sentences: number;
  /** Words per sentence, one decimal; 0 when there are no sentences. */
  averageSentence: number;
  /** Flesch–Kincaid grade level, one decimal, never below 0; null for fewer than 10 words. */
  grade: number | null;
}

/**
 * Word and sentence counts and the Flesch–Kincaid grade level:
 * 0.39 × (words ÷ sentences) + 11.8 × (syllables ÷ words) − 15.59 (Kincaid et al., 1975).
 * It is a rough guide to sentence and word length, built for English, and says nothing
 * about whether the explanation is right.
 */
export function explanationStats(text: string): ExplanationStats {
  const words = text.match(WORD_PATTERN) ?? [];
  const endings = text.match(SENTENCE_END)?.length ?? 0;
  const trailing = ENDS_WITH_WORD.test(text.trim()) && words.length > 0;
  const sentences = Math.max(
    endings + (trailing ? 1 : 0),
    words.length > 0 ? 1 : 0
  );
  if (words.length === 0) {
    return { words: 0, sentences: 0, averageSentence: 0, grade: null };
  }
  const syllables = words.reduce((sum, word) => sum + countSyllables(word), 0);
  const perSentence = words.length / sentences;
  const grade =
    words.length < 10
      ? null
      : Math.max(
          0,
          Math.round(
            (0.39 * perSentence + 11.8 * (syllables / words.length) - 15.59) *
              10
          ) / 10
        );
  return {
    words: words.length,
    sentences,
    averageSentence: Math.round(perSentence * 10) / 10,
    grade,
  };
}

/* ------------------------------------------------------------------ saving + export */

export interface FeynmanNotes {
  concept: string;
  source: string;
  explanation: string;
  sourceTerms: string;
  gaps: string;
  missed: string;
  simple: string;
}

export const EMPTY_NOTES: FeynmanNotes = {
  concept: "",
  source: "",
  explanation: "",
  sourceTerms: "",
  gaps: "",
  missed: "",
  simple: "",
};

const NOTE_KEYS = Object.keys(EMPTY_NOTES) as (keyof FeynmanNotes)[];

/** Reads saved notes, keeping only string fields. */
export function parseSavedNotes(raw: string | null): FeynmanNotes {
  if (!raw) {
    return { ...EMPTY_NOTES };
  }
  try {
    const data: unknown = JSON.parse(raw);
    if (typeof data !== "object" || data === null) {
      return { ...EMPTY_NOTES };
    }
    const record = data as Record<string, unknown>;
    const notes = { ...EMPTY_NOTES };
    for (const key of NOTE_KEYS) {
      const value = record[key];
      if (typeof value === "string") {
        notes[key] = value.slice(0, 20_000);
      }
    }
    return notes;
  } catch {
    return { ...EMPTY_NOTES };
  }
}

/** The four steps as plain text, skipping empty ones. */
export function notesText(
  notes: FeynmanNotes,
  labels: Record<
    "concept" | "source" | "explanation" | "gaps" | "missed" | "simple",
    string
  >
): string {
  const parts: [string, string][] = [
    [labels.concept, notes.concept],
    [labels.source, notes.source],
    [labels.explanation, notes.explanation],
    [labels.gaps, notes.gaps],
    [labels.missed, notes.missed],
    [labels.simple, notes.simple],
  ];
  return parts
    .filter(([, value]) => value.trim() !== "")
    .map(([label, value]) => `${label}\n${value.trim()}`)
    .join("\n\n");
}
